import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { usePageEnter } from "@/v2/lib/motion";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import BrandLockup from "@/v2/components/BrandLockup";
import { isAdminUser, isDeptManager } from "@/v2/lib/rbac";
import WelcomeApproved from "@/v2/components/WelcomeApproved";
import { useMasarAccess, tamayozHome } from "@/masar/lib";
import {
  Home, Map, Trophy, Award, Bell, User, LogOut, Rocket, LifeBuoy, LayoutDashboard, Activity, Building2,
  Compass, Route, Tags, LayoutGrid, BookOpen, ArrowLeft,
} from "lucide-react";

const links = [
  { to: "/dashboard", label: "الرئيسية", icon: Home, end: true },
  { to: "/journey", label: "دوراتي", icon: Map },
  { to: "/notifications", label: "الإشعارات", icon: Bell },
  { to: "/profile", label: "الملف الشخصي", icon: User },
];

// قائمة مسار المختصرة: لا إشعارات ولا نبض تعلّم ولا كتالوج برامج
const masarLinks = [
  { to: "/masar", label: "نظرة مساري", short: "نظرة مساري", icon: LayoutGrid, end: true },
  { to: "/masar/plan", label: "مساري الوظيفي", short: "مساري", icon: Route },
  { to: "/masar/certificates/guide", label: "دليل الشهادات", short: "الدليل", icon: BookOpen },
  { to: "/masar/certificates", label: "سجل الشهادات", short: "شهاداتي", icon: Award, end: true },
  { to: "/masar/offers", label: "عروض التدريب", short: "العروض", icon: Tags },
];

/** مفتاح «مسار / تميز»: حاوية واحدة، الخيار النشط بحد ذهبي — نفس الحساب والجلسة */
export const PlatformSwitch = ({ isMasar, onSwitch, compact = false, testId = "platform-switch" }) => {
  const opt = (active, label, Icon, target) => (
    <button key={label} onClick={() => !active && onSwitch(target)} aria-pressed={active} data-testid={`${testId}-${target === "masar" ? "masar" : "tamayoz"}`}
      className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg font-semibold transition-colors duration-200 ${compact ? "h-8 px-2 text-xs" : "h-10 px-3 text-sm"}
        ${active ? "bg-surface text-ink border border-line" : "border border-transparent text-ink-2 hover:text-ink"}`}>
      <Icon size={compact ? 13 : 16} /> {label}
    </button>
  );
  return (
    <div className={`flex gap-1 rounded-xl bg-surface-2 ${compact ? "p-0.5" : "p-1"}`} role="group" aria-label="التبديل بين المنصتين" data-testid={testId}>
      {opt(isMasar, "مسار", Compass, "masar")}
      {opt(!isMasar, "تميز", BookOpen, "tamayoz")}
    </div>
  );
};

export default function V2Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isMasar = pathname.startsWith("/masar");
  const pageScope = usePageEnter(pathname);
  const { data: challenges = [] } = useQuery({ queryKey: ["lessons"], queryFn: async () => (await api.get("/lessons")).data, enabled: !isMasar });

  const current = challenges.find((c) => c.status === "in_progress") || challenges.find((c) => c.status === "available");
  const gotoToday = () => (current ? navigate(`/journey/${current.id}`) : navigate("/journey"));
  const doLogout = () => { logout(); navigate("/login"); };

  const navItems = [...links];
  if (isDeptManager(user)) navItems.push({ to: "/admin", label: "متابعة نطاقي", icon: Building2 });
  if (isAdminUser(user)) navItems.push({ to: "/admin", label: "مركز التحكم", icon: LayoutDashboard });
  const { data: masarAccess } = useMasarAccess(!!user);
  const masarItems = [...masarLinks];
  if (masarAccess?.any) masarItems.push({ to: "/masar/admin", label: user.supervision ? "متابعة نطاقي" : "لوحة التحكم", icon: LayoutDashboard });
  const items = isMasar ? masarItems : navItems;
  const switchPlatform = (target) => navigate(target === "masar" ? "/masar" : tamayozHome(user));
  const isWideArea = pathname.startsWith("/admin") || pathname.startsWith("/dept") || pathname.startsWith("/masar/admin");

  return (
    <div className="v2-root workspace-root" dir="rtl">
      {!isMasar && <WelcomeApproved />}

      {/* desktop sidebar (right for RTL) */}
      <aside className="hidden lg:flex flex-col fixed bottom-0 right-0 w-72 z-30 top-0">
        <div className="workspace-sidebar-panel theme-navy h-full flex flex-col px-5 pt-6 pb-5">
          <div className="workspace-brand"><BrandLockup tone="dark" stacked /></div>
          <div className="mt-3 px-1 text-xs text-ink-3" data-testid="platform-caption">{isMasar ? "منصة مسار — المسار الوظيفي والجدارات" : "منصة تميز — التعلّم"}</div>
          {isMasar ? (
            <button onClick={() => navigate("/masar")} data-testid="v2-today-btn" className="v2-btn my-3 w-full">
              <ArrowLeft size={18} /> تابع خطوتك التالية
            </button>
          ) : (
            <button onClick={gotoToday} data-testid="v2-today-btn" className="v2-btn my-4 w-full">
              <Rocket size={20} /> {current?.status === "in_progress" ? "تابع درسك" : "ابدأ التعلّم"}
            </button>
          )}
          <nav className="workspace-nav flex-1 space-y-0.5 overflow-y-auto -mx-1 px-1">
            {items.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.end} data-testid={`v2nav-${l.to.slice(1).replace(/\//g, "-") || "home"}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 h-11 rounded-lg transition-colors ${
                    isActive ? "text-ink font-semibold" : "text-ink-2 hover:text-ink hover:bg-surface-2"
                  }`}>
                <l.icon size={19} strokeWidth={1.75} /> {l.label}
              </NavLink>
            ))}
            <button onClick={() => toast.info("للدعم تواصل مع إدارة المنصة: support@masar.sa")} className="w-full flex items-center gap-3 px-4 h-11 rounded-lg text-ink-2 hover:text-ink hover:bg-surface-2">
              <LifeBuoy size={19} strokeWidth={1.75} /> الدعم والمساعدة
            </button>
          </nav>
          <div className="pt-4 mt-2 bg-surface-2/50 -mx-5 px-5 -mb-5 pb-5">
            <div className="flex items-center gap-3 px-1 mb-3">
              <div className="relative">
                {user?.avatar ? <img src={user.avatar} alt={user.name} className="w-10 h-10 rounded-full object-cover border border-beige/40" />
                  : <div className="w-10 h-10 rounded-full bg-beige/20 border border-beige/40 flex items-center justify-center font-extrabold text-beige-ink">{(user?.name || "؟").trim().charAt(0)}</div>}
              </div>
              <div className="min-w-0">
                <div className="font-bold truncate text-sm">{user?.name}</div>
                <div className="text-xs v2-gold-text font-bold truncate">{user?.department || "موظف"}</div>
              </div>
            </div>
            <button onClick={doLogout} data-testid="v2-logout" className="w-full flex items-center gap-2 justify-center h-10 rounded-lg text-ink-2 hover:text-ink hover:bg-surface-2 border border-line">
              <LogOut size={18} /> تسجيل الخروج
            </button>
            <div className="mt-3 text-center text-xs text-ink-3 mb-1.5">مسارك هنا، وتعلّمك في تميز</div>
            <PlatformSwitch isMasar={isMasar} onSwitch={switchPlatform} />
          </div>
        </div>
      </aside>

      {/* mobile top bar */}
      <header className="workspace-mobile-header lg:hidden sticky top-0 z-30 px-3 flex items-center justify-between gap-2">
        <div className={`min-w-0 overflow-hidden ${isMasar ? "hidden sm:block" : ""}`}><BrandLockup tone="light" /></div>
        {isMasar && <div className="sm:hidden brand-lockup" data-tone="light"><span className="haramain-wordmark" role="img" aria-label="الحرمين" /></div>}
        <div className="workspace-mobile-actions flex items-center gap-1.5">
          <PlatformSwitch isMasar={isMasar} onSwitch={switchPlatform} compact testId="platform-switch-mobile" />
          {isDeptManager(user) && !isMasar && (
            <button onClick={() => navigate("/dept")} data-testid="v2-mobile-dept" className="w-10 h-10 rounded-full bg-surface-2 border border-line flex items-center justify-center"><Building2 size={18} /></button>
          )}
          {isAdminUser(user) && !isMasar && (
            <button onClick={() => navigate("/admin")} data-testid="v2-mobile-admin" className="w-10 h-10 rounded-full bg-surface-2 border border-line flex items-center justify-center"><LayoutDashboard size={18} /></button>
          )}
          {isMasar && masarAccess?.any && (
            <button onClick={() => navigate("/masar/admin")} data-testid="v2-mobile-masar-admin" className="w-10 h-10 rounded-full bg-surface-2 border border-line flex items-center justify-center"><LayoutDashboard size={18} /></button>
          )}
          {!isMasar && <button onClick={() => navigate("/notifications")} className="w-10 h-10 rounded-full bg-surface-2 border border-line flex items-center justify-center"><Bell size={18} /></button>}
          <button onClick={doLogout} className="w-10 h-10 rounded-full bg-surface-2 border border-line flex items-center justify-center"><LogOut size={18} /></button>
        </div>
      </header>

      {/* content */}
      <main className={`workspace-main lg:mr-72 px-4 sm:px-8 pt-6 pb-28 lg:pb-10 mx-auto relative z-10 ${isWideArea ? "max-w-[1400px]" : "max-w-6xl"}`}>
                {!user?.work_type && <div className="v2-inset p-3 mb-4 text-sm flex items-center justify-between gap-3"><span>حدد طبيعة عملك في إعدادات الحساب لإظهار البرامج المناسبة لك.</span><button className="v2-btn-ghost v2-btn-sm" onClick={() => navigate("/profile")}>إعدادات حسابي</button></div>}
        <div ref={pageScope}>
          <Outlet />
        </div>
      </main>

      {/* mobile bottom nav */}
      <nav className="workspace-mobile-nav lg:hidden fixed bottom-0 inset-x-0 z-40 px-2 flex items-center justify-around">
        {isMasar ? (
          <>
            {[masarLinks[0], masarLinks[1]].map((l) => <BottomItem key={l.to} l={l} />)}
            <button onClick={() => navigate("/masar")} className="relative -mt-7 w-14 h-14 rounded-full v2-btn !p-0 !min-h-0" data-testid="v2-today-fab" aria-label="نظرة مساري"><Compass size={26} /></button>
            {[masarLinks[3], masarLinks[4]].map((l) => <BottomItem key={l.to} l={l} />)}
          </>
        ) : (
          <>
            {[links[0], links[1]].map((l) => <BottomItem key={l.to} l={l} />)}
            <button onClick={gotoToday} className="relative -mt-7 w-14 h-14 rounded-full v2-btn !p-0 !min-h-0" data-testid="v2-today-fab">
              <Rocket size={26} />
            </button>
            {[links[2], links[3]].map((l) => <BottomItem key={l.to} l={l} />)}
          </>
        )}
      </nav>
    </div>
  );
}

const BottomItem = ({ l }) => (
  <NavLink to={l.to} end={l.end}
    className={({ isActive }) => `flex flex-col items-center gap-1 text-xs w-16 ${isActive ? "text-ink font-semibold" : "text-ink-3"}`}>
    <l.icon size={22} /> {l.short || l.label.split(" ")[0]}
  </NavLink>
);
